import React from 'react';
import { cva } from "class-variance-authority";
import { cn } from "../../utils/cn";
import Icon from '../AppIcon';

// ===== CARD VARIANTS SYSTEM ===== //
const cardVariants = cva(
  "bg-card border border-border rounded-lg transition-all duration-300 ease-out",
  {
    variants: {
      variant: {
        default: "border-border shadow-sm hover:shadow-md",
        elevated: "shadow-md hover:shadow-lg border-border/50",
        interactive: "cursor-pointer hover:shadow-lg hover:scale-[1.02] border-border hover:border-primary/20",
        outline: "border-2 border-border bg-transparent",
        ghost: "border-transparent shadow-none bg-muted/50",
        gradient: "bg-gradient-to-br from-card to-muted/30 border-border/50 shadow-sm",
        feature: "border-primary/20 bg-primary/5 shadow-sm hover:shadow-md hover:border-primary/30",
        success: "border-success/20 bg-success/5 shadow-sm",
        warning: "border-warning/20 bg-warning/5 shadow-sm",
        error: "border-error/20 bg-error/5 shadow-sm",
        premium: "bg-gradient-to-br from-primary/5 to-accent/5 border-accent/20 shadow-md"
      },
      size: {
        sm: "card-spacing-sm",
        default: "card-spacing-md",
        md: "card-spacing-lg", 
        lg: "card-spacing-xl",
        xl: "p-10"
      },
      radius: {
        none: "rounded-none",
        sm: "rounded-sm",
        default: "rounded-lg",
        md: "rounded-xl",
        lg: "rounded-2xl",
        full: "rounded-3xl"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      radius: "default"
    }
  }
);

// ===== BASE CARD COMPONENT ===== //
export const Card = React.forwardRef(({ 
  className, 
  variant, 
  size, 
  radius,
  children,
  onClick,
  ...props 
}, ref) => {
  const Comp = onClick ? "button" : "div";
  
  return (
    <Comp
      ref={ref}
      className={cn(
        cardVariants({ variant, size, radius }), 
        // Animation patterns
        "transition-all animate-normal animate-ease-out",
        "animate-hover-lift",
        className
      )}
      onClick={onClick}
      {...props}
    >
      {children}
    </Comp>
  );
});

Card.displayName = "Card";

// ===== CARD HEADER ===== //
export const CardHeader = React.forwardRef(({ 
  className, 
  children,
  ...props 
}, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 pb-4", className)}
    {...props}
  >
    {children}
  </div>
));

CardHeader.displayName = "CardHeader";

// ===== CARD TITLE ===== //
export const CardTitle = React.forwardRef(({ 
  className, 
  children,
  ...props 
}, ref) => (
  <h3
    ref={ref}
    className={cn("text-heading-md font-semibold text-text-primary leading-none tracking-tight", className)}
    {...props}
  >
    {children}
  </h3>
));

CardTitle.displayName = "CardTitle";

// ===== CARD DESCRIPTION ===== //
export const CardDescription = React.forwardRef(({ 
  className, 
  children,
  ...props 
}, ref) => (
  <p
    ref={ref}
    className={cn("text-body-sm text-secondary", className)}
    {...props}
  >
    {children}
  </p>
));

CardDescription.displayName = "CardDescription";

// ===== CARD CONTENT ===== //
export const CardContent = React.forwardRef(({ 
  className, 
  children,
  ...props 
}, ref) => (
  <div
    ref={ref}
    className={cn("space-y-4", className)}
    {...props}
  >
    {children}
  </div>
));

CardContent.displayName = "CardContent";

// ===== CARD FOOTER ===== //
export const CardFooter = React.forwardRef(({ 
  className, 
  children,
  align = "right",
  ...props 
}, ref) => {
  const alignmentClasses = {
    left: "justify-start",
    center: "justify-center",
    right: "justify-end",
    between: "justify-between"
  };

  return (
  <div
    ref={ref}
      className={cn(
        "flex items-center pt-4 border-t border-border",
        alignmentClasses[align],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});

CardFooter.displayName = "CardFooter";

// ===== SPECIALIZED CARD COMPONENTS ===== //

// ===== FEATURE CARD ===== //
export const FeatureCard = ({ 
  icon,
  iconColor = "primary",
  title,
  description,
  features = [],
  action,
  className,
  ...props 
}) => {
  return (
    <Card variant="feature" className={cn("h-full", className)} {...props}>
      <CardHeader>
        <div className="flex items-center space-x-3 mb-3">
          <div className={cn(
            "w-12 h-12 rounded-lg flex items-center justify-center",
            iconColor === "primary" && "bg-primary/10 text-primary",
            iconColor === "accent" && "bg-accent/10 text-accent",
            iconColor === "success" && "bg-success/10 text-success"
          )}>
            <Icon name={icon} size={24} />
          </div>
          <CardTitle>{title}</CardTitle>
        </div>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      
      {features.length > 0 && (
        <CardContent>
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center space-x-2 text-body-sm">
                <Icon name="Check" size={16} className="text-success flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      )}
      
      {action && (
        <CardFooter>
          {action}
        </CardFooter>
      )}
    </Card>
  );
};

// ===== METRIC CARD ===== //
export const MetricCard = ({ 
  title,
  value,
  subtitle,
  icon,
  trend,
  trendDirection = "up",
  className,
  ...props 
}) => {
  return (
    <Card variant="elevated" className={cn("text-center", className)} {...props}>
      <CardContent className="space-y-3">
        {icon && (
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name={icon} size={24} className="text-primary" />
          </div>
        )}
        
        <div className="space-y-1">
          <p className="text-display-md font-bold text-text-primary">{value}</p>
          <p className="text-body-sm font-medium text-text-primary">{title}</p>
          {subtitle && (
            <p className="text-caption text-secondary">{subtitle}</p>
          )}
        </div>
        
        {trend && (
          <div className={cn(
            "flex items-center justify-center space-x-1 text-body-xs",
            trendDirection === "up" ? "text-success" : "text-error"
          )}>
            <Icon 
              name={trendDirection === "up" ? "TrendingUp" : "TrendingDown"} 
              size={14} 
            />
            <span>{trend}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ===== TESTIMONIAL CARD ===== //
export const TestimonialCard = ({ 
  quote,
  author,
  role,
  company,
  avatar,
  rating,
  className,
  ...props 
}) => {
  return (
    <Card variant="elevated" className={cn("h-full", className)} {...props}>
      <CardContent className="space-y-4">
        {rating && (
          <div className="flex space-x-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <Icon
                key={i}
                name="Star"
                size={16}
                className={i < rating ? "text-warning fill-current" : "text-muted"}
              />
            ))}
          </div>
        )}
        
        <blockquote className="text-body-md italic text-primary leading-relaxed">
          "{quote}"
        </blockquote>
        
        <div className="flex items-center space-x-3">
          {avatar && (
            <img 
              src={avatar} 
              alt={author}
              className="w-10 h-10 rounded-full object-cover"
            />
          )}
          <div>
            <p className="text-body-sm font-semibold text-text-primary">{author}</p>
            <p className="text-caption text-secondary">{role}</p>
            {company && (
              <p className="text-caption text-secondary">{company}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ===== SERVICE CARD ===== //
export const ServiceCard = ({ 
  title,
  description,
  icon,
  features = [],
  price,
  priceSubtext,
  action,
  popular = false,
  className,
  ...props 
}) => {
  return (
    <Card 
      variant={popular ? "premium" : "interactive"}
      className={cn("h-full relative", className)} 
      {...props}
    >
      {popular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-accent text-accent-foreground px-3 py-1 rounded-full text-caption font-semibold">
            Most Popular
          </span>
        </div>
      )}
      
      <CardHeader>
        <div className="flex items-center space-x-3 mb-3">
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name={icon} size={24} className="text-primary" />
          </div>
          <div>
            <CardTitle>{title}</CardTitle>
            {price && (
              <div className="mt-1">
                <span className="text-heading-lg font-bold text-text-primary">{price}</span>
                {priceSubtext && (
                  <span className="text-caption text-secondary ml-1">{priceSubtext}</span>
                )}
              </div>
            )}
          </div>
        </div>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      
      <CardContent>
        <ul className="space-y-3">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start space-x-3">
              <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
              <span className="text-body-sm">{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>
      
      {action && (
        <CardFooter>
          {action}
        </CardFooter>
      )}
    </Card>
  );
};

// ===== BLOG/ARTICLE CARD ===== //
export const ArticleCard = ({ 
  title,
  excerpt,
  author,
  date,
  readTime,
  image,
  tags = [],
  href,
  className,
  ...props 
}) => {
  const CardComponent = href ? "a" : "div";
  
  return (
    <Card 
      variant="interactive" 
      className={cn("h-full overflow-hidden", className)}
      {...props}
    >
      <CardComponent href={href} className="block h-full">
        {image && (
          <div className="aspect-video bg-muted overflow-hidden">
            <img 
              src={image} 
              alt={title}
              className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            />
          </div>
        )}
        
        <div className="p-6 space-y-4">
          <div className="space-y-2">
            <h3 className="text-heading-sm font-semibold text-text-primary leading-tight line-clamp-2">
              {title}
            </h3>
            <p className="text-body-sm text-secondary line-clamp-3">
              {excerpt}
            </p>
          </div>
          
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {tags.map((tag, index) => (
                <span 
                  key={index}
                  className="px-2 py-1 bg-muted rounded text-caption font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
          
          <div className="flex items-center justify-between text-caption text-secondary">
            <span>{author}</span>
            <div className="flex items-center space-x-2">
              <span>{date}</span>
              {readTime && (
                <>
                  <span>•</span>
                  <span>{readTime} min read</span>
                </>
              )}
            </div>
          </div>
        </div>
      </CardComponent>
    </Card>
  );
};